export type AnyRec = Record<string, any>;

export interface IAttrFileName {
  file_name?: string;
}

export interface IPhotoFlag {
  dc_id?: number;
  id?: number | string;
  access_hash?: string | number;
  mime_type?: string;
  sizes?: Array<{ size2?: number }>;
  predicate_name?: string; // "photo"
  message?: string; // інколи підпис сидить тут
  attributes?: IAttrFileName[];
}

export interface IDocument {
  dc_id?: number;
  id?: number | string;
  access_hash?: string | number;
  mime_type?: string; // "video/*", "image/gif", ...
  size2_INT32?: number;
  size2_INT64?: number;
  sizes?: number | Array<any>;
  predicate_name?: string; // "document"
  message?: string; // підпис
  attributes?: IAttrFileName[];
}

export interface IMessageMedia {
  predicate_name?: string; // "messageMediaPhoto" | "messageMediaDocument" | ...
  caption?: string; // іноді підпис тут
  photo_FLAGPHOTO?: IPhotoFlag;
  document?: IDocument;
}

export interface IContent {
  // те, що приходить у різних гілках
  messageId?: number;
  input?: string;
  admin_id?: string | number;

  outMessage?: {
    message?: string;
    out?: boolean; // важливо для визначення "свій/чужий"
    from_id?: { user_id?: number };
    peer_id?: { user_id?: number };
  };

  from_id?: { user_id?: number };
  peer_id?: { user_id?: number };
  peer?: {
    user_id?: number;
    access_hash?: string | number;
    peer_id?: number;
    peer_type?: number;
    self_id?: number;
  };

  // медіа може бути як на корені, так і в media.*
  document?: IDocument;
  photo_FLAGPHOTO?: IPhotoFlag;

  media?: IMessageMedia;

  // інше
  predicate_name?: string;
  media_access_hash?: string;
  webpage?: { display_url?: string; url?: string };
}

export const isObject = (v: unknown): v is AnyRec => typeof v === 'object' && v !== null;

export const hasProp = <K extends string>(o: unknown, k: K): o is AnyRec & Record<K, unknown> =>
  isObject(o) && k in o;

export const getActiveChatId = (): number | null => {
  try {
    const url = new URL(window.location.href);
    const id = url.searchParams.get('id'); // якщо інший параметр — зміни тут
    return id ? Number(id) : null;
  } catch {
    return null;
  }
};

export const normalizePayloadFromRes = (res: IContent) => {
  const media = res.media ?? {};

  // 1) текст/підпис у різних місцях
  const caption =
    media.caption ??
    media.document?.message ??
    media.photo_FLAGPHOTO?.message ??
    res.document?.message ??
    res.photo_FLAGPHOTO?.message ??
    undefined;

  const messageText =
    res.outMessage?.message ??
    (res as any)?.message ?? // інколи лежить на корені
    caption ??
    '';

  // 2) джерела медіа
  const photo = media.photo_FLAGPHOTO ?? res.photo_FLAGPHOTO;
  const doc = media.document ?? res.document;

  // 3) тип (узагальнений)
  let type: string | undefined;
  if (photo) {
    type = 'photo';
  } else if (doc?.mime_type) {
    if (doc.mime_type.startsWith('video/')) type = 'video';
    else if (doc.mime_type.startsWith('audio/')) type = 'audio';
    else if (doc.mime_type === 'image/gif') type = 'gif';
    else type = 'document';
  } else if (media.predicate_name) {
    type = media.predicate_name.replace(/^messageMedia/i, '').toLowerCase(); // photo|document|...
  }

  // 4) MIME
  const mimeType = photo?.mime_type ?? doc?.mime_type ?? undefined;

  // 5) HASH (тримай як рядок, щоб не ловити проблем з big-int)
  const rawHash = photo?.access_hash ?? doc?.access_hash ?? undefined;
  const hash = rawHash != null ? String(rawHash) : undefined;

  // 6) file_name
  const attrs = (doc?.attributes ?? []) as IAttrFileName[];
  const fileName = attrs.find((a) => a?.file_name)?.file_name ?? undefined;

  return { messageText, type, mimeType, hash, fileName };
};

// 1) Створити Unix-час у секундах (типу "1755095173")
export const toUnixSeconds = (date: Date = new Date()): number => Math.floor(date.getTime() / 1000);

export const getLocalTimeZone = (): string =>
  Intl.DateTimeFormat().resolvedOptions().timeZone || 'UTC';

/** Повертає "HH:mm" або "HH:mm AM/PM" (за замовчуванням) */
export const formatUnixToTime = (
  unix: number | string,
  tz: string = getLocalTimeZone(),
  withMeridiem: boolean = true,
  locale: string = 'en-GB',
): string => {
  const n = typeof unix === 'string' ? Number(unix.trim()) : unix;
  const ms = n < 1e12 ? n * 1000 : n; // якщо секунди — множимо
  const d = new Date(ms);

  const parts = Object.fromEntries(
    new Intl.DateTimeFormat(locale, {
      timeZone: tz,
      hour: '2-digit',
      minute: '2-digit',
      hour12: false, // беремо 24h і самі додаємо AM/PM
    })
      .formatToParts(d)
      .map((p) => [p.type, p.value]),
  );

  const h24 = Number(parts.hour);
  const ampm = h24 >= 12 ? 'PM' : 'AM';

  return withMeridiem
    ? `${parts.hour}:${parts.minute} ${ampm}` // напр. "17:30 PM"
    : `${parts.hour}:${parts.minute}`; // напр. "17:30"
};
